import { forwardRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { layoutContentStyles } from './styles/layout-content.styles';
import type { LayoutContentPropsInterface } from './types/layout-content';

/**
 * Layout content is a specialized grid item used primarily for visually containing content within
 * the context of a `Page` component
 * (i.e. providing a background and padding). If you need grid functionality without any
 * visual treatment, you can simply use a `Grid` component
 * instead.
 *
 * @since 10.0.0
 */
export const LayoutContent = forwardRef<HTMLDivElement, LayoutContentPropsInterface>(
  (props: LayoutContentPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, ...rest } = props;
    const styles = css(layoutContentStyles, cssProp);
    const Component = asChild ? Slot : 'div';
    return (
      <Component
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Layout.Content"
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

LayoutContent.displayName = 'Layout.Content';
